import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import { E as EGDSLink, b as EGDSLinkSizes } from '../link/index-chunk.esm.js';
import { c as classNames, u as useEGDSContext, d as EGDSContextProvider } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-expando.css';
import { a as EGDSIcon } from '../icon/index-chunk.esm.js';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';

const EGDSExpandoTriggerStateMap = [
    ['expand', 'opened'],
    ['collapse', 'closed'],
];
const EGDSExpandoTriggerStates = new Map(EGDSExpandoTriggerStateMap);

const getClassNameList$2 = classNames.bind(styles);
const ExpandoPeek = (props) => {
    const { isVisible = false, expandLabel, expandA11yLabel, collapseLabel, collapseA11yLabel, l10n, handleClick, hideScrim, analytics, className, children, lines, items, peekControlRef, revealRef, ...attrs } = props;
    const forwardContext = useEGDSContext();
    const { formatText } = useLocalization();
    const innerDomRef = useRef(null);
    const [expandoHeightRef, setExpandoHeightRef] = useState({
        scrollHeight: 0,
        clientHeight: 0,
    });
    const expandoPeekInnerClassName = getClassNameList$2('uitk-expando-peek-inner', {
        'display-lines': lines,
        'display-items': items,
    });
    useEffect(() => {
        innerDomRef?.current &&
            setExpandoHeightRef({
                scrollHeight: innerDomRef?.current?.scrollHeight,
                clientHeight: innerDomRef?.current?.clientHeight,
            });
    }, [innerDomRef]);
    useEffect(() => {
        if (isVisible) {
            revealRef?.current.focus();
        }
    }, [isVisible, revealRef]);
    const renderExpandoButton = () => {
        const btnText = isVisible
            ? collapseLabel || l10n.formatText('expando.collapseLabel')
            : expandLabel || l10n.formatText('expando.expandLabel');
        const btnA11yText = isVisible
            ? collapseA11yLabel || l10n.formatText('expando.collapseLabel')
            : expandA11yLabel || l10n.formatText('expando.expandLabel');
        const localAnalyticsId = isVisible
            ? EGDSExpandoTriggerStates.get('collapse')
            : EGDSExpandoTriggerStates.get('expand');
        const btnClass = getClassNameList$2('uitk-expando-peek-link');
        const controlClasses = getClassNameList$2('uitk-expando-peek-control', {
            'uitk-expando-peek-control-active': isVisible,
        });
        return (jsx("div", { className: controlClasses, children: jsx(EGDSLink, { "aria-expanded": isVisible, className: btnClass, analytics: analytics && {
                    localAnalyticsId,
                    ...analytics,
                }, domRef: peekControlRef, children: jsx("button", { type: "button", onClick: handleClick, "aria-label": btnA11yText, children: btnText }) }) }));
    };
    const wrapperClasses = getClassNameList$2('uitk-expando-peek', className);
    let childCount = React.Children.count(children);
    if (childCount === 1) {
        childCount = React.Children.count(children.props.children);
    }
    const expandoCustomProperties = {
        '--uitk-expando-peek-lines-to-clamp': lines,
    };
    // Disable if content is smaller than the expandedHeight
    // To fix the vrbo issue https://github.expedia.biz/egds/components-react/issues/1486
    const isScrollable = expandoHeightRef.scrollHeight - expandoHeightRef.clientHeight;
    const adjustedScrollHeight = expandoHeightRef.scrollHeight - (isScrollable === 1 ? 1 : 0);
    const showExpando = !!((lines && expandoHeightRef.clientHeight < adjustedScrollHeight) ||
        (items && items < childCount));
    const mainClass = 'uitk-expando-peek-main';
    const mainClasses = getClassNameList$2(mainClass, {
        [`${mainClass}-active`]: isVisible,
        [`${mainClass}-inactive`]: !isVisible && showExpando,
        'hide-scrim': hideScrim,
    });
    const contextData = {
        collapsed: !isVisible,
        srMessage: formatText('radio.button.hidden.items', items, childCount),
        ...forwardContext.data,
    };
    return (jsx(Fragment, { children: jsxs("div", { className: wrapperClasses, ...attrs, children: [jsx("div", { className: mainClasses, children: jsx("div", { className: expandoPeekInnerClassName, ref: innerDomRef, style: expandoCustomProperties, "data-items-to-display": isVisible ? undefined : items, children: jsx(EGDSContextProvider, { config: { context: 'uitk-expando-peek-context', data: contextData }, children: children }) }) }), showExpando && renderExpandoButton()] }) }));
};
const EGDSExpandoPeek = (props) => {
    const l10n = useLocalization();
    return jsx(ExpandoPeek, { l10n: l10n, ...props });
};

const getClassNameList$1 = classNames.bind(styles);
/**
 * This is a simple expander that when clicked, will show more text or other
 * children. Makes use of inversion of control.
 * We make use of a description list:
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/HTML/Element/dl}
 * Accessibility:
 * @see {@link http://wwwexpediacom.a11y-demos-web.us-west-2.test.expedia.com/pages/components/toggles}
 */
const EGDSExpandoList = ({ children, className, ...props }) => {
    const cssClassNames = getClassNameList$1('uitk-expando-list', className);
    return (jsx("section", { className: cssClassNames, ...props, children: children }));
};
EGDSExpandoList.displayName = 'EGDSExpandoList';

const getClassNameList = classNames.bind(styles);
/**
 * This component contains a details disclosure which when clicked will expand and show a
 * hidden description. Expansion is determined by the boolean value of {@code isVisible}.
 *
 * @param className the provided classes to be used in both the title and the description
 *
 * In the case that the user disabled JavaScript, the details disclosure default behaviour
 * will be active. This is akin to the JavaScript enabled behaviour.
 */
const getTestId = (testId, isOpen) => (isOpen ? `${testId}-expanded` : `${testId}-collapsed`);
const EGDSExpando = ({ analytics, alignment, expandoDescription, expandoTitle, className, children, isVisible, isDisabled, isIndented = true, onToggle, triggerPosition, type, size = EGDSLinkSizes.get('small'), collapsedAccessibilityText, expandedAccessibilityText, ...attrs }) => {
    const localAnalyticsId = isVisible
        ? EGDSExpandoTriggerStates.get('collapse')
        : EGDSExpandoTriggerStates.get('expand');
    const onToggleHandler = (event) => {
        if (event?.currentTarget?.open !== isVisible) {
            onToggle?.();
            const expandoAnalytics = analytics && {
                localAnalyticsId,
                ...analytics,
            };
            expandoAnalytics && analyticsTracker(expandoAnalytics);
        }
    };
    const expandoTriggerIcon = (jsx(EGDSIcon, { className: getClassNameList(`uitk-expando-trigger-icon${isVisible ? ' uitk-expando-trigger-icon-active' : ''}`), "data-testid": isVisible ? 'uitk-expando-button-icon-expanded' : undefined, name: "expand_more" }));
    const a11yLabel = isVisible ? expandedAccessibilityText : collapsedAccessibilityText;
    const title = (jsxs(Fragment, { children: [triggerPosition === 'leading' && expandoTriggerIcon, jsx("span", { className: getClassNameList('uitk-expando-title'), "aria-label": a11yLabel || undefined, children: expandoTitle }), triggerPosition === 'trailing' && expandoTriggerIcon] }));
    return (jsxs("details", { open: isDisabled ? !isDisabled : isVisible, onToggle: onToggleHandler, className: getClassNameList('uitk-expando', `uitk-expando-${type === 'list' ? 'list-item' : type}`, className, {
            'is-disabled': isDisabled,
        }), ...attrs, children: [jsx("summary", { className: getClassNameList('uitk-expando-trigger-content'), tabIndex: isDisabled ? -1 : undefined, children: type === 'link' ? (jsx(EGDSLink, { size: size, className: getClassNameList('uitk-expando-link'), align: alignment === 'trailing' ? 'right' : undefined, children: jsx("span", { children: title }) })) : (title) }), jsx("div", { className: getClassNameList('uitk-expando-body', { [`is-indented-${triggerPosition}`]: isIndented }), "data-testid": getTestId(`uitk-expando-${type}-body`, isVisible), children: jsx("div", { className: getClassNameList('uitk-expando-description'), children: expandoDescription ?? children }) })] }));
};
EGDSExpando.displayName = 'EGDSExpando';

const EGDSExpandoListItem = (props) => {
    const { triggerPosition = 'leading', ...otherProps } = props;
    return jsx(EGDSExpando, { ...otherProps, triggerPosition: triggerPosition, type: "list" });
};
EGDSExpandoListItem.displayName = 'EGDSExpandoListItem';

const EGDSExpandoLink = (props) => {
    const { triggerPosition = 'trailing', expandoTitle: collapsedLabel, expandedLabel = collapsedLabel, isVisible, ...otherProps } = props;
    return (jsx(EGDSExpando, { ...otherProps, isVisible: isVisible, expandoTitle: isVisible ? expandedLabel : collapsedLabel, triggerPosition: triggerPosition, type: "link" }));
};
EGDSExpandoLink.displayName = 'EGDSExpandoLink';

export { ExpandoPeek as E, EGDSExpandoPeek as a, EGDSExpandoLink as b, EGDSExpando as c, EGDSExpandoList as d, EGDSExpandoListItem as e };
//# sourceMappingURL=index-chunk.esm.js.map
